import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Pencil, Trash } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

const levelFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  schoolId: z.string().min(1, "School is required"),
  sequenceOrder: z.number().min(1).max(100),
  numberOfRanks: z.number().min(1).max(10),
});

export function LevelManager() {
  const [selectedSchool, setSelectedSchool] = useState<string>("all");
  const [levelToDelete, setLevelToDelete] = useState<any | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<any | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof levelFormSchema>>({
    resolver: zodResolver(levelFormSchema),
    defaultValues: {
      name: "",
      description: "",
      schoolId: "",
      sequenceOrder: 1,
      numberOfRanks: 1,
    },
  });

  const { data: schools } = useQuery({
    queryKey: ['schools'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('schools')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  const { data: levels, isLoading } = useQuery({
    queryKey: ['levels', selectedSchool],
    queryFn: async () => {
      let query = supabase
        .from('levels')
        .select(`
          *,
          schools(name)
        `)
        .order('sequence_order');
      
      if (selectedSchool !== 'all') {
        query = query.eq('school_id', selectedSchool);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const createLevel = useMutation({
    mutationFn: async (values: z.infer<typeof levelFormSchema>) => {
      const { error } = await supabase
        .from('levels')
        .insert({
          name: values.name,
          description: values.description || null,
          school_id: values.schoolId,
          sequence_order: values.sequenceOrder,
          number_of_ranks: values.numberOfRanks,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['levels'] });
      form.reset();
      setIsDialogOpen(false);
      toast({
        title: "Success",
        description: "Level created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create level",
        variant: "destructive",
      });
    },
  });

  const updateLevel = useMutation({
    mutationFn: async (values: z.infer<typeof levelFormSchema>) => {
      const { error } = await supabase
        .from('levels')
        .update({
          name: values.name,
          description: values.description || null,
          school_id: values.schoolId,
          sequence_order: values.sequenceOrder,
          number_of_ranks: values.numberOfRanks,
        })
        .eq('id', selectedLevel.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['levels'] });
      form.reset();
      setIsDialogOpen(false);
      setSelectedLevel(null);
      toast({
        title: "Success",
        description: "Level updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update level",
        variant: "destructive",
      });
    },
  });

  const deleteLevel = useMutation({
    mutationFn: async (levelId: string) => {
      if (!levelId) {
        throw new Error('No level selected for deletion');
      }

      const { error } = await supabase
        .from('levels')
        .delete()
        .eq('id', levelId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['levels'] });
      setLevelToDelete(null);
      toast({
        title: "Success",
        description: "Level deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete level",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (level: any) => {
    form.reset({
      name: level.name,
      description: level.description || "",
      schoolId: level.school_id,
      sequenceOrder: level.sequence_order,
      numberOfRanks: level.number_of_ranks,
    });
    setSelectedLevel(level);
    setIsDialogOpen(true);
  };

  const onSubmit = (values: z.infer<typeof levelFormSchema>) => {
    if (selectedLevel) {
      updateLevel.mutate(values);
    } else {
      createLevel.mutate(values);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Select value={selectedSchool} onValueChange={setSelectedSchool}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select School" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Schools</SelectItem>
            {schools?.map((school) => (
              <SelectItem key={school.id} value={school.id}>
                {school.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>Add Level</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {selectedLevel ? "Edit Level" : "Add New Level"}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="schoolId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>School</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a school" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {schools?.map((school) => (
                            <SelectItem key={school.id} value={school.id}>
                              {school.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sequenceOrder"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sequence Order</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          {...field} 
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="numberOfRanks"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Number of Ranks</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          {...field} 
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button 
                    type="submit" 
                    disabled={createLevel.isPending || updateLevel.isPending}
                  >
                    {(createLevel.isPending || updateLevel.isPending) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {selectedLevel ? "Update Level" : "Create Level"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>School</TableHead>
              <TableHead>Sequence</TableHead>
              <TableHead>Ranks</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {levels?.map((level) => (
              <TableRow key={level.id}>
                <TableCell>{level.name}</TableCell>
                <TableCell>{level.description || '-'}</TableCell>
                <TableCell>{level.schools?.name}</TableCell>
                <TableCell>{level.sequence_order}</TableCell>
                <TableCell>{level.number_of_ranks}</TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(level)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setLevelToDelete(level)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AlertDialog open={!!levelToDelete} onOpenChange={() => setLevelToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this level
              and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                if (levelToDelete?.id) {
                  deleteLevel.mutate(levelToDelete.id);
                }
              }}
              disabled={deleteLevel.isPending}
            >
              {deleteLevel.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}